/**
 * Gyro Admin's people picker (components/PeoplePicker.tsx; owner, 2026-10-08: "SHOULD BE ABLE TO CHOOSE USERS IN GYRO
 * ADMIN"): the arithmetic over a one-address-per-line value, and the shape GET /v1/admin/people answers. The picker edits
 * the same text the form always held, so validation, the patch and the service's refusals are unchanged.
 */

export interface KnownPerson {
  /** Lower-cased. */
  email: string;
}

/** GET /v1/admin/people (services/gyro-svc/src/adminPeople.ts). */
export interface PeopleSearch {
  /** Sorted by e-mail, at most the limit asked for. */
  people: KnownPerson[];
  /** Every match, beyond the limit too. */
  total: number;
  /** False when the desktop app's users could not be read or the usage ledger has not loaded: someone may be missing. */
  complete: boolean;
}

/** The most people one search asks for (the service's PEOPLE_LIMIT_MAX): more than Gyro knows, so every match arrives. */
export const PEOPLE_LIMIT = 500;
/** The longest search text the service reads (its PEOPLE_QUERY_MAX). */
export const PEOPLE_QUERY_MAX = 200;

/** The shape the PPP settings accept (lib/ppp.ts, and the service's ppp/pppTypes.ts lowerEmail). */
const EMAIL = /^[^\s@]+@[^\s@]+$/;

/** True for an address the service accepts. */
export function isAddress(value: string): boolean {
  return value.length <= 320 && EMAIL.test(value);
}

/**
 * True when the part after the '@' has a dot (name@exante360.com). The service accepts "zlongo@ex", but it is far more
 * likely an address still being typed than a real one.
 */
export function looksFinished(email: string): boolean {
  return email.slice(email.indexOf('@') + 1).includes('.');
}

/** The value's lines in order, trimmed, one per distinct line (any case), blank lines dropped. */
export function peopleIn(value: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of value.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || seen.has(line.toLowerCase())) continue;
    seen.add(line.toLowerCase());
    out.push(line);
  }
  return out;
}

/** The value with `emails` added at the end, lower-cased, skipping any already there (any case). */
export function withPeople(value: string, emails: readonly string[]): string {
  return peopleIn([...peopleIn(value), ...emails.map((email) => email.trim().toLowerCase())].join('\n')).join('\n');
}

/** The value without `email` (any case). */
export function withoutPerson(value: string, email: string): string {
  const drop = email.trim().toLowerCase();
  return peopleIn(value).filter((line) => line.toLowerCase() !== drop).join('\n');
}

/** What the admin typed or pasted, read as addresses. */
export interface Typed {
  /** Every address, lower-cased, in order, one each. */
  people: string[];
  /** The words with an '@' that are still not an address the service accepts (a doubled '@', too long), as typed, one each. */
  rejected: string[];
  /**
   * The entries of a list (split on ';' and new lines, as Outlook separates them) with no word that has something on both
   * sides of an '@': a name with no address ("Cy Dee"), or "bo@". Trimmed, as typed, one each. Only a paste reports them;
   * a single search is not a list.
   */
  unmatched: string[];
}

const ADDRESS_WORD = /[^\s<>,;"'()[\]]+@[^\s<>,;"'()[\]]+/g;

/**
 * Every address in what the admin typed or pasted: a single address, a list split by commas, semicolons, spaces or lines,
 * and Outlook's "Name <address>" form all work. A leading "mailto:" and a trailing full stop are dropped. A word with an
 * '@' that is not an address is reported in `rejected`, and a list entry with no such word at all in `unmatched`.
 */
export function typedPeople(text: string): Typed {
  const people = new Set<string>();
  const rejected = new Set<string>();
  for (const match of text.matchAll(ADDRESS_WORD)) {
    const email = match[0].replace(/^mailto:/i, '').replace(/\.+$/, '').toLowerCase();
    if (isAddress(email)) people.add(email);
    else rejected.add(match[0]);
  }
  const unmatched = new Set<string>();
  for (const raw of text.split(/[;\n]/)) {
    const entry = raw.trim();
    if (entry && !new RegExp(ADDRESS_WORD.source).test(entry)) unmatched.add(entry);
  }
  return { people: [...people], rejected: [...rejected], unmatched: [...unmatched] };
}

/**
 * True for text that is a pasted list rather than a search: more than one address (or word with an '@'), or a separator
 * (, ; < > or a new line). The picker does not search for it; Enter or Add takes every address in it.
 */
export function isPaste(text: string, typed: Typed = typedPeople(text)): boolean {
  return typed.people.length + typed.rejected.length > 1 || /[,;<>\n]/.test(text);
}
